// ON-association discriminator for the predicate-head slot recognizer
// (`editorContextSlot.predicateHead.ts`) — C3 ship-gate remediation. Split
// out to keep the recognizer ≤200 lines, mirroring the
// `editorContextSlot.predicateHeadScan.ts` split.
//
// `ON` is the most overloaded keyword the recognizer touches: besides the
// join/MERGE predicate head it introduces upsert clauses (`INSERT … ON
// CONFLICT`, `INSERT … ON DUPLICATE KEY UPDATE`), JSON_TABLE column defaults
// (`NULL ON EMPTY`, `ERROR ON ERROR`), FK actions (`REFERENCES p ON DELETE`),
// index/backup options (`DROP_EXISTING = ON`), and object targets (`GRANT …
// ON`, `CREATE INDEX … ON`). The C1 gate bar (PH-D2) only protected the
// UNKNOWN-clause path — but the clause machine names `join-on` for ANY `ON`
// met while the frame is in `'from'` (`editorContextClause.ts:268`), so an
// upsert `ON` after `INSERT … SELECT … FROM src` sailed through the named
// override. The discriminator therefore lives HERE, at the recognizer: an
// `'on-head'` requires a genuine, still-unconsumed JOIN (or a MERGE's USING)
// association, established by a backward token walk that works at the typing
// moment — no lookahead past the cursor, because at `ON ▮` there is nothing
// after the cursor yet.
//
// Walk rules (C3 re-review revision — the first walk's "first `ON` met →
// refuse" missed three shapes; see the D1–D3 blocks in the truth table).
// Backward from the ON at its own paren depth, with a pending-ON counter:
//   - `ON` met            → pendingOn++ (that predicate consumed SOME join
//                           behind it — keep walking to find whose);
//   - an ON-OWNING join   → if pendingOn > 0, it is the consumed one:
//                           pendingOn--, continue; else it owns OUR ON →
//                           genuine. Ownership is engine-aware: plain/INNER/
//                           LEFT/RIGHT/FULL[OUTER] joins own everywhere;
//                           mysql `STRAIGHT_JOIN` (an identifier to the
//                           clause tokenizer — not in its keyword list) owns
//                           when preceded by a table reference; `CROSS JOIN`
//                           owns ONLY on mysql/sqlite, where it is an INNER
//                           JOIN synonym that takes ON (verified live) — on
//                           postgres/mssql/oracle it takes no condition;
//   - a NON-owning join   → `NATURAL … JOIN` (no engine lets it take ON;
//                           NATURAL beats CROSS in `NATURAL CROSS JOIN`,
//                           which sqlite parses) — skipped entirely, the
//                           counter untouched, so an upsert `ON` behind it
//                           still refuses;
//   - `USING` met         → a MERGE's source (`MERGE INTO t USING s ON ▮`)
//                           owns our ON iff pendingOn === 0 — walking past
//                           consumed source joins first is what admits the
//                           T-SQL joined, unparenthesized source (`USING sa
//                           JOIN sb ON … ON ▮`, executes on live mssql); a
//                           join column-list `USING (…)` consumes a join
//                           exactly like an ON → pendingOn++;
//   - crossing OUT of the enclosing paren group → refuse (a JOIN outside the
//                           group cannot associate with an ON inside it —
//                           this is what refuses JSON_TABLE's
//                           `… NULL ON ▮EMPTY` inside COLUMNS (…));
//   - statement start reached → refuse (`INSERT INTO t VALUES (1) ON ▮`,
//                           bare `ON ▮`, object-target shapes, upsert `ON`
//                           whose joins are all consumed or non-owning).
// Conservative by construction: a refusal keeps today's clause
// classification, exactly like every other PH-D6 refusal.

import type { ClauseToken } from './editorContextClause';

// Join-type keywords that may sit between a join's `JOIN` token and the
// table reference before it (`NATURAL LEFT OUTER JOIN`, `CROSS JOIN`, …).
const JOIN_TYPE_PREFIXES = new Set(['INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS', 'NATURAL']);

/** True when the `ON` at `tokens[onIdx]` genuinely heads a JOIN/MERGE
 *  predicate (see the walk rules above). Engine-aware: CROSS JOIN ownership
 *  and `STRAIGHT_JOIN` are dialect facts, not syntax facts. */
export function hasJoinOrMergeAssociation(
  tokens: readonly ClauseToken[],
  onIdx: number,
  engine: string,
): boolean {
  let depth = 0;
  let pendingOn = 0;
  for (let i = onIdx - 1; i >= 0; i -= 1) {
    const tok = tokens[i]!;
    if (tok.kind === 'rparen') { depth += 1; continue; }
    if (tok.kind === 'lparen') {
      depth -= 1;
      if (depth < 0) return false;
      continue;
    }
    if (depth > 0) continue;
    if (
      tok.kind === 'identifier' &&
      engine === 'mysql' &&
      tok.text.toUpperCase() === 'STRAIGHT_JOIN' &&
      isTableRefEnd(tokens[i - 1])
    ) {
      // mysql joined_table: `table_reference STRAIGHT_JOIN table_factor
      // [join_specification]` — takes ON (verified live). The table-ref
      // guard excludes the OTHER `STRAIGHT_JOIN`: the `SELECT STRAIGHT_JOIN
      // …` optimizer hint, which follows the SELECT keyword instead.
      if (pendingOn > 0) { pendingOn -= 1; continue; }
      return true;
    }
    if (tok.kind !== 'keyword') continue;
    const kw = tok.text.toUpperCase();
    if (kw === 'JOIN') {
      if (!joinCanOwnOn(tokens, i, engine)) continue;
      if (pendingOn > 0) { pendingOn -= 1; continue; }
      return true;
    }
    if (kw === 'ON') { pendingOn += 1; continue; }
    if (kw === 'USING') {
      if (isMergeUsing(tokens, i)) return pendingOn === 0;
      pendingOn += 1;
      continue;
    }
  }
  return false;
}

/** Whether the join whose `JOIN` keyword sits at `tokens[joinIdx]` can own an
 *  `ON` condition. Scans the contiguous join-type prefix keywords before it:
 *  `NATURAL` never owns (any engine — sqlite even parses `NATURAL CROSS
 *  JOIN`, still refusing its ON); `CROSS` owns only where CROSS JOIN is an
 *  INNER JOIN synonym (mysql, sqlite — both verified live; postgres, mssql
 *  and oracle all reject `CROSS JOIN … ON` as a syntax error). */
function joinCanOwnOn(
  tokens: readonly ClauseToken[],
  joinIdx: number,
  engine: string,
): boolean {
  let hasCross = false;
  for (let i = joinIdx - 1; i >= 0; i -= 1) {
    const tok = tokens[i]!;
    if (tok.kind !== 'keyword') break;
    const kw = tok.text.toUpperCase();
    if (!JOIN_TYPE_PREFIXES.has(kw)) break;
    if (kw === 'NATURAL') return false;
    if (kw === 'CROSS') hasCross = true;
  }
  if (hasCross) return engine === 'mysql' || engine === 'sqlite';
  return true;
}

/** A join operator must follow a table reference — its final token is an
 *  identifier (bare, quoted, alias) or the `)` of a derived table /
 *  `PARTITION (…)` selection. */
function isTableRefEnd(tok: ClauseToken | undefined): boolean {
  return tok !== undefined && (tok.kind === 'identifier' || tok.kind === 'rparen');
}

/** Distinguishes a MERGE's `USING source` (which introduces the ON predicate)
 *  from a join's column-list `USING (…)` (which consumes the join instead):
 *  backward from the USING at its own depth, `MERGE` met before `JOIN`/`FROM`
 *  wins. `MERGE [TOP (n)] [INTO] target [AS alias] USING …` shapes all reach
 *  MERGE through identifier/INTO/AS tokens and balanced groups. */
function isMergeUsing(tokens: readonly ClauseToken[], usingIdx: number): boolean {
  let depth = 0;
  for (let i = usingIdx - 1; i >= 0; i -= 1) {
    const tok = tokens[i]!;
    if (tok.kind === 'rparen') { depth += 1; continue; }
    if (tok.kind === 'lparen') {
      depth -= 1;
      if (depth < 0) return false;
      continue;
    }
    if (depth > 0 || tok.kind !== 'keyword') continue;
    const kw = tok.text.toUpperCase();
    if (kw === 'MERGE') return true;
    if (kw === 'JOIN' || kw === 'FROM') return false;
  }
  return false;
}
